import React, { useState, useEffect } from 'react';
import {
  Home,
  User,
  ShoppingBag,
  Calendar,
  MessageSquare,
  Wallet,
  MessageCircle,
  LogOut,
  X,
  Menu,
  ChevronRight
} from 'lucide-react';
import TrripLogo from '../../assets/Trrip_logo/4triplogo_white.png';

const MobileNav = ({
  portalType = 'activity',
  subtitle,
  activeTab = '',
  navigate,
  handleLogout,
  userData = {},
  isMenuOpen: externalIsOpen,
  setIsMenuOpen: externalSetIsOpen
}) => {
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);

  const isDrawerOpen = externalIsOpen !== undefined ? externalIsOpen : internalIsOpen;
  const setIsDrawerOpen = externalSetIsOpen || setInternalIsOpen;

  const getDisplaySubtitle = () => {
    if (subtitle && subtitle.toLowerCase() !== 'management') {
      return subtitle.toUpperCase();
    }
    const typeMap = {
      restaurant: 'RESTAURANT MANAGEMENT',
      activity: 'ACTIVITY MANAGEMENT',
      shop: 'RETAIL SHOP MANAGEMENT',
    };
    return typeMap[(portalType || 'activity').toLowerCase()] || `${(portalType || 'partner').toUpperCase()} MANAGEMENT`;
  };

  useEffect(() => {
    const scrollContainer = document.getElementById('dashboard-scroll-container') || window;
    
    const handleScroll = (e) => {
      const currentScrollY = scrollContainer === window ? window.scrollY : scrollContainer.scrollTop;
      if (currentScrollY > lastScrollY && currentScrollY > 30) {
        setIsVisible(false);
      } else {
        setIsVisible(true);
      }
      setLastScrollY(currentScrollY);
    };

    scrollContainer.addEventListener('scroll', handleScroll, { passive: true });
    return () => scrollContainer.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);

  // Lock body scroll when drawer is open
  useEffect(() => {
    if (isDrawerOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isDrawerOpen]);

  const pType = (portalType || 'activity').toLowerCase();

  // Bottom quick nav bar items (5 tabs max)
  const getBottomNavItems = () => {
    if (pType === 'shop') {
      return [
        { id: 'home', label: 'Home', icon: Home, path: '' },
        { id: 'listing', label: 'Products', icon: ShoppingBag, path: 'listing' },
        { id: 'booking', label: 'Orders', icon: Calendar, path: 'booking' },
        { id: 'reviews', label: 'Reviews', icon: MessageSquare, path: 'reviews' },
        { id: 'menu', label: 'Menu', icon: Menu, isToggle: true },
      ];
    } else if (pType === 'restaurant') {
      return [
        { id: 'home', label: 'Home', icon: Home, path: '' },
        { id: 'profile', label: 'Profile', icon: User, path: 'profile' },
        { id: 'booking', label: 'Reservations', icon: Calendar, path: 'booking' },
        { id: 'reviews', label: 'Reviews', icon: MessageSquare, path: 'reviews' },
        { id: 'menu', label: 'Menu', icon: Menu, isToggle: true },
      ];
    } else {
      // Activity
      return [
        { id: 'home', label: 'Home', icon: Home, path: '' },
        { id: 'listing', label: 'Items', icon: ShoppingBag, path: 'listing' },
        { id: 'booking', label: 'Reservations', icon: Calendar, path: 'booking' },
        { id: 'reviews', label: 'Reviews', icon: MessageSquare, path: 'reviews' },
        { id: 'menu', label: 'Menu', icon: Menu, isToggle: true },
      ];
    }
  };

  // Full Sidebar Options for Mobile Drawer Menu
  const getDrawerMenuItems = () => {
    if (pType === 'shop') {
      return [
        { id: 'home', label: 'Dashboard', icon: Home, path: '' },
        { id: 'profile', label: 'Shop Profile', icon: User, path: 'profile' },
        { id: 'listing', label: 'Shop Products', icon: ShoppingBag, path: 'listing' },
        { id: 'booking', label: 'Shop Orders', icon: Calendar, path: 'booking' },
        { id: 'reviews', label: 'Customer Reviews', icon: MessageSquare, path: 'reviews' },
        { id: 'wallet', label: 'Wallet', icon: Wallet, path: 'wallet' },
        { id: 'support', label: 'Support', icon: MessageCircle, path: 'support' },
      ];
    } else if (pType === 'restaurant') {
      return [
        { id: 'home', label: 'Dashboard', icon: Home, path: '' },
        { id: 'profile', label: 'Restaurant Profile', icon: User, path: 'profile' },
        { id: 'booking', label: 'Reservations', icon: Calendar, path: 'booking' },
        { id: 'reviews', label: 'Guest Reviews', icon: MessageSquare, path: 'reviews' },
        { id: 'wallet', label: 'Wallet', icon: Wallet, path: 'wallet' },
        { id: 'support', label: 'Support', icon: MessageCircle, path: 'support' },
      ];
    } else {
      // Activity
      return [
        { id: 'home', label: 'Dashboard', icon: Home, path: '' },
        { id: 'profile', label: 'Activity Profile', icon: User, path: 'profile' },
        { id: 'listing', label: 'Activity Items', icon: ShoppingBag, path: 'listing' },
        { id: 'booking', label: 'Reservations', icon: Calendar, path: 'booking' },
        { id: 'reviews', label: 'Guest Reviews', icon: MessageSquare, path: 'reviews' },
        { id: 'wallet', label: 'Wallet', icon: Wallet, path: 'wallet' },
        { id: 'support', label: 'Support', icon: MessageCircle, path: 'support' },
      ];
    }
  };

  const bottomItems = getBottomNavItems();
  const drawerItems = getDrawerMenuItems();

  const handleNavClick = (path) => {
    setIsDrawerOpen(false);
    if (navigate) {
      navigate(path);
    }
  };

  return (
    <>
      {/* Mobile Floating Bottom Bar */}
      <div
        className={`lg:hidden fixed bottom-4 left-1/2 -translate-x-1/2 w-[90%] max-w-sm z-[90] transition-all duration-300 ease-in-out ${
          isVisible ? 'translate-y-0 opacity-100' : 'translate-y-[200%] opacity-0'
        }`}
      >
        <nav className="bg-white/95 backdrop-blur-2xl border border-slate-200/90 shadow-[0_8px_25px_rgba(0,0,0,0.12)] rounded-2xl flex items-center justify-around p-1 ring-1 ring-black/5">
          {bottomItems.map((item) => {
            const Icon = item.icon;
            const isActive = activeTab === item.id;
            const isMenuBtn = item.isToggle;
            const isSelected = isActive || (isMenuBtn && isDrawerOpen);

            return (
              <button
                key={item.id}
                onClick={() => {
                  if (isMenuBtn) {
                    setIsDrawerOpen(!isDrawerOpen);
                  } else {
                    handleNavClick(item.path);
                  }
                }}
                className="flex flex-1 flex-col items-center justify-center py-1.5 px-1 rounded-xl transition-all duration-200 group relative"
              >
                <div
                  className={`p-1.5 rounded-xl transition-all duration-200 ${
                    isSelected
                      ? 'bg-[#067978]/10 text-[#067978] scale-105'
                      : 'text-slate-400 group-hover:text-slate-600 group-hover:bg-slate-50'
                  }`}
                >
                  <Icon
                    size={18}
                    strokeWidth={isSelected ? 2.2 : 1.8}
                    className="transition-transform duration-200"
                  />
                </div>

                <span
                  className={`text-[9px] font-bold uppercase tracking-tight mt-0.5 transition-colors duration-200 ${
                    isSelected ? 'text-[#067978]' : 'text-slate-400 group-hover:text-slate-600'
                  }`}
                >
                  {item.label}
                </span>

                {/* Minimal Active Bottom Dot Indicator */}
                {isSelected && !isMenuBtn && (
                  <span className="w-1 h-1 rounded-full bg-[#067978] mt-0.5 animate-pulse"></span>
                )}

                {isMenuBtn && (
                  <span className="absolute top-1.5 right-3 w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
                )}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Mobile Drawer Overlay Backdrop */}
      {isDrawerOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-slate-950/60 backdrop-blur-sm z-[150] transition-opacity duration-300"
          onClick={() => setIsDrawerOpen(false)}
        />
      )}

      {/* Mobile Drawer Menu Slide-Over Panel (Opens on Left Side) */}
      <aside
        className={`lg:hidden fixed inset-y-0 left-0 max-w-[300px] w-full bg-white shadow-2xl z-[160] flex flex-col transition-transform duration-300 ease-out transform ${
          isDrawerOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        {/* Drawer Header with Logo Only */}
        <div className="bg-[#067978] p-5 text-white flex items-center justify-between shadow-md relative overflow-hidden flex-shrink-0">
          {/* Background Decorative Accent */}
          <div className="absolute top-0 left-0 w-32 h-32 bg-white/10 rounded-full blur-2xl pointer-events-none"></div>

          <div className="flex items-center gap-3 relative z-10">
            <img
              src={TrripLogo}
              alt="Trrip Logo"
              className="h-8 w-auto object-contain brightness-0 invert drop-shadow-sm"
            />
            <div className="w-[1px] h-8 bg-white/30 rounded-full shrink-0"></div>
            <div className="flex flex-col justify-center">
              <span className="text-sm font-medium text-white tracking-tight leading-none">Partner Portal</span>
              <span className="text-[9px] font-extrabold text-emerald-100/80 uppercase tracking-widest mt-1 leading-none">
                {getDisplaySubtitle()}
              </span>
            </div>
          </div>

          <button
            onClick={() => setIsDrawerOpen(false)}
            className="w-9 h-9 rounded-xl bg-white/15 hover:bg-white/25 flex items-center justify-center text-white transition-colors relative z-10"
            aria-label="Close menu"
          >
            <X size={20} />
          </button>
        </div>

        {/* Drawer Navigation Options List */}
        <nav className="flex-1 p-4 space-y-1.5 overflow-y-auto">
          <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] px-3 mb-2">
            Main Menu
          </p>

          {drawerItems.map((item) => {
            const Icon = item.icon;
            const isActive = activeTab === item.id;

            return (
              <button
                key={item.id}
                onClick={() => handleNavClick(item.path)}
                className={`w-full flex items-center justify-between px-4 py-3 rounded-xl text-xs font-bold transition-all duration-200 group ${
                  isActive
                    ? 'bg-[#067978] text-white shadow-md shadow-teal-900/20'
                    : 'text-slate-600 hover:bg-slate-100 hover:text-[#067978]'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div
                    className={`p-1.5 rounded-lg transition-colors ${
                      isActive ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-500 group-hover:bg-teal-50 group-hover:text-[#067978]'
                    }`}
                  >
                    <Icon size={18} />
                  </div>
                  <span>{item.label}</span>
                </div>
                <ChevronRight
                  size={16}
                  className={`transition-transform group-hover:translate-x-0.5 ${
                    isActive ? 'text-white/80' : 'text-slate-300 group-hover:text-[#067978]'
                  }`}
                />
              </button>
            );
          })}
        </nav>

        {/* Drawer Footer with Logout */}
        {handleLogout && (
          <div className="p-4 border-t border-slate-100 bg-slate-50/50 flex-shrink-0">
            <button
              onClick={() => {
                setIsDrawerOpen(false);
                handleLogout();
              }}
              className="w-full flex items-center justify-center gap-2.5 px-4 py-3 text-xs font-black text-rose-600 bg-rose-50 hover:bg-rose-100 active:bg-rose-200 rounded-xl transition-all shadow-sm border border-rose-100"
            >
              <LogOut size={18} className="text-rose-500" />
              <span>Logout from Account</span>
            </button>
          </div>
        )}
      </aside>
    </>
  );
};

export default MobileNav;
